/**
 * SearchPanel — sidebar panel for global search across sessions and workspace files.
 *
 * Sends search queries to Engine via search/query RPC and renders streaming results.
 * Engine 未连接时搜索面板显示「引擎未连接」提示。
 */

import {
    CaseSensitive,
    ChevronRight,
    FolderOpen,
    Loader2,
    Regex,
    Search,
    WholeWord,
    X,
    Zap,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { cn } from "../lib/utils";
import { useSearchStore } from "../store/search-store";
import {
    getThemeAccentStyle,
    type ThemeAccent,
} from "../theme/runtime/theme-accents";
import { SidebarCloseButton } from "./SidebarCloseButton";

/* ── matchKind Badge ── */

const MATCH_KIND_BADGE: Record<
    string,
    { label: string; accent: ThemeAccent } | null
> = {
    heading: { label: "H", accent: "info" },
    config: { label: "CFG", accent: "muted" },
    output: { label: "OUT", accent: "success" },
    "definition-id": {
        label: "D",
        accent: "define",
    },
    "plugin-name": {
        label: "P",
        accent: "for",
    },
    "render-ref": { label: "R", accent: "warning" },
    ref: { label: "$", accent: "refScope" },
    "for-id": { label: "F", accent: "for" },
    "for-variable": { label: "V", accent: "refLoop" },
    "for-source": { label: "O", accent: "info" },
    "for-inner-plugin": { label: "FP", accent: "for" },
    "content-id": { label: "C", accent: "content" },
    "use-id": { label: "U", accent: "use" },
    "use-source": { label: "UF", accent: "use" },
    "import-namespace": { label: "I", accent: "import" },
    "import-source": { label: "IF", accent: "success" },
    "meta-domain": { label: "M", accent: "muted" },
    markdown: null,
};

interface SearchPanelProps {
    autoFocus?: boolean;
    className?: string;
    showHeader?: boolean;
    touch?: boolean;
}

const SearchPanelTouchContext = createContext(false);

function useSearchPanelTouch() {
    return useContext(SearchPanelTouchContext);
}

/* ── Search Input ── */

function SearchInput({ autoFocus = true }: { autoFocus?: boolean }) {
    const t = useT("editor");
    const query = useSearchStore((s) => s.query);
    const setQuery = useSearchStore((s) => s.setQuery);
    const options = useSearchStore((s) => s.options);
    const setOptions = useSearchStore((s) => s.setOptions);
    const executeSearch = useSearchStore((s) => s.executeSearch);
    const cancelSearch = useSearchStore((s) => s.cancelSearch);
    const isSearching = useSearchStore((s) => s.isSearching);
    const inputRef = useRef<HTMLInputElement>(null);
    const touch = useSearchPanelTouch();

    // Debounced search
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const handleQueryChange = useCallback(
        (value: string) => {
            setQuery(value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (value.trim()) {
                    executeSearch();
                }
            }, 300);
        },
        [setQuery, executeSearch],
    );

    // Focus input on mount
    useEffect(() => {
        if (!autoFocus) return;
        inputRef.current?.focus();
    }, [autoFocus]);

    // Listen for focus event from keyboard shortcut
    useEffect(() => {
        const handler = () => inputRef.current?.focus();
        window.addEventListener("magic-md:search-focus", handler);
        return () => window.removeEventListener("magic-md:search-focus", handler);
    }, []);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                if (e.shiftKey) {
                    useSearchStore.getState().prevMatch();
                } else {
                    useSearchStore.getState().nextMatch();
                }
            } else if (e.key === "Escape") {
                if (!query) {
                    // Close sidebar if empty
                } else {
                    // Cancel and clear
                    cancelSearch();
                    setQuery("");
                }
            }
        },
        [query, cancelSearch, setQuery],
    );

    const [showFilters, setShowFilters] = useState(false);

    return (
        <div className={touch ? "px-4 pt-3 pb-3 space-y-3" : "px-3 pt-3 pb-2 space-y-2"}>
            {/* Search input with inline option toggles */}
            <div
                className={cn(
                    "flex items-center border border-border bg-background focus-within:ring-1 focus-within:ring-primary/50",
                    touch ? "h-11 rounded-xl" : "h-7 rounded",
                )}
            >
                <Search
                    size={touch ? 17 : 14}
                    className={cn(
                        "shrink-0 text-muted-foreground pointer-events-none",
                        touch ? "ml-3" : "ml-2",
                    )}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => handleQueryChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("search.placeholder")}
                    className={cn(
                        "flex-1 min-w-0 h-full bg-transparent font-mono focus:outline-none placeholder:text-muted-foreground/50",
                        touch ? "px-2 text-[16px]" : "px-1.5 text-[11px]",
                    )}
                />
                {isSearching && (
                    <button
                        type="button"
                        title={t("search.cancel")}
                        onClick={cancelSearch}
                        className={cn(
                            "shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 cursor-pointer",
                            touch ? "mr-1 h-8 w-8 rounded-lg" : "mr-0.5 h-5 w-5 rounded-sm",
                        )}
                    >
                        <X size={touch ? 16 : 12} />
                    </button>
                )}
                {/* Inline option toggles */}
                <div className={cn("flex shrink-0", touch ? "mr-1.5 gap-1" : "mr-1 gap-0.5")}>
                    <ToggleButton
                        active={options.caseSensitive}
                        title={t("search.caseSensitive")}
                        onClick={() =>
                            setOptions({ caseSensitive: !options.caseSensitive })
                        }
                    >
                        <CaseSensitive size={14} />
                    </ToggleButton>
                    <ToggleButton
                        active={options.wholeWord}
                        title={t("search.wholeWord")}
                        onClick={() => setOptions({ wholeWord: !options.wholeWord })}
                    >
                        <WholeWord size={14} />
                    </ToggleButton>
                    <ToggleButton
                        active={options.useRegex}
                        title={t("search.regex")}
                        onClick={() => setOptions({ useRegex: !options.useRegex })}
                    >
                        <Regex size={14} />
                    </ToggleButton>
                </div>
            </div>

            {/* Scope toggle */}
            <div className={cn("flex gap-1", touch && "rounded-xl bg-muted/35 p-1")}>
                <ScopeButton
                    active={options.scope === "sessions"}
                    label={t("search.scope.session")}
                    icon={<Zap size={12} />}
                    onClick={() => {
                        setOptions({ scope: "sessions" });
                        // Cancel current search & re-execute with new scope
                        cancelSearch();
                        if (query.trim()) {
                            // Defer to next tick so state updates first
                            setTimeout(() => useSearchStore.getState().executeSearch(), 0);
                        }
                    }}
                />
                <ScopeButton
                    active={options.scope === "workspace"}
                    label={t("search.scope.workspace")}
                    icon={<FolderOpen size={12} />}
                    onClick={() => {
                        setOptions({ scope: "workspace" });
                        cancelSearch();
                        if (query.trim()) {
                            setTimeout(() => useSearchStore.getState().executeSearch(), 0);
                        }
                    }}
                />
            </div>

            {/* File filter toggle */}
            <button
                type="button"
                className={cn(
                    "text-muted-foreground hover:text-foreground cursor-pointer",
                    touch ? "text-[12px]" : "text-[10px]",
                )}
                onClick={() => setShowFilters(!showFilters)}
            >
                {t("search.fileFilter")}
            </button>
            {showFilters && (
                <div className="space-y-1.5">
                    <input
                        type="text"
                        value={options.includeGlob}
                        onChange={(e) => setOptions({ includeGlob: e.target.value })}
                        placeholder={t("search.includePlaceholder")}
                        className={cn(
                            "w-full rounded border border-border bg-background font-mono focus:outline-none focus:ring-1 focus:ring-primary/50 placeholder:text-muted-foreground/50",
                            touch ? "h-10 px-3 text-[14px]" : "h-6 px-2 text-[10px]",
                        )}
                    />
                    <input
                        type="text"
                        value={options.excludeGlob}
                        onChange={(e) => setOptions({ excludeGlob: e.target.value })}
                        placeholder={t("search.excludePlaceholder")}
                        className={cn(
                            "w-full rounded border border-border bg-background font-mono focus:outline-none focus:ring-1 focus:ring-primary/50 placeholder:text-muted-foreground/50",
                            touch ? "h-10 px-3 text-[14px]" : "h-6 px-2 text-[10px]",
                        )}
                    />
                </div>
            )}
        </div>
    );
}

/* ── Toggle Button ── */

function ToggleButton({
    active,
    title,
    onClick,
    children,
}: {
    active: boolean;
    title: string;
    onClick: () => void;
    children: React.ReactNode;
}) {
    const touch = useSearchPanelTouch();

    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className={cn(
                "flex items-center justify-center transition-colors cursor-pointer",
                touch ? "h-8 w-8 rounded-lg" : "h-5 w-5 rounded-sm",
                active
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            )}
        >
            {children}
        </button>
    );
}

/* ── Scope Button ── */

function ScopeButton({
    active,
    label,
    icon,
    onClick,
}: {
    active: boolean;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
}) {
    const touch = useSearchPanelTouch();

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "flex items-center gap-1 transition-colors",
                touch ? "flex-1 justify-center rounded-lg px-2 py-2 text-[12px]" : "rounded px-2 py-1 text-[10px]",
                active
                    ? "bg-primary/15 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            )}
        >
            {icon}
            {label}
        </button>
    );
}

/* ── Search Result Tree ── */

function SearchResultTree() {
    const results = useSearchStore((s) => s.results);
    const touch = useSearchPanelTouch();

    if (results.length === 0) {
        return null;
    }

    return (
        <div className={cn("flex-1 overflow-y-auto", touch ? "text-[13px]" : "text-[11px]")}>
            {results.map((file) => (
                <FileGroup key={file.filePath} file={file} />
            ))}
        </div>
    );
}

/* ── File Group ── */

function FileGroup({
    file,
}: {
    file: import("@magic-md/engine-client/protocol").FileSearchResult;
}) {
    const collapsed = useSearchStore((s) => s.collapsedFiles.has(file.filePath));
    const toggleCollapse = useSearchStore((s) => s.toggleFileCollapse);
    const touch = useSearchPanelTouch();

    const fileName = file.fileName;
    const dirPath = file.filePath.replace(/\/[^/]+$/, "");

    return (
        <div>
            <button
                type="button"
                className={cn(
                    "flex items-center w-full gap-1.5 hover:bg-muted/60 font-medium cursor-pointer",
                    touch ? "px-4 py-2.5 text-[13px]" : "px-3 py-1 text-[11px]",
                )}
                onClick={() => toggleCollapse(file.filePath)}
            >
                <ChevronRight
                    size={touch ? 14 : 12}
                    className={cn(
                        "transition-transform shrink-0",
                        !collapsed && "rotate-90",
                    )}
                />
                <span className="shrink-0 text-foreground">{fileName}</span>
                <span className={cn("text-muted-foreground/60 truncate min-w-0", touch ? "text-[10px]" : "text-[9px]")}>
                    {dirPath}
                </span>
                <span className={cn("shrink-0 ml-auto text-muted-foreground bg-muted/50 rounded px-1", touch ? "text-[10px]" : "text-[9px]")}>
                    {file.matches.length}
                </span>
                {file.source === "session" && (
                    <span
                        className={cn("theme-accent-text", touch ? "text-[10px]" : "text-[9px]")}
                        style={getThemeAccentStyle("warning")}
                    >
                        ⚡
                    </span>
                )}
            </button>

            {!collapsed &&
                file.matches.map((match, i) => (
                    <MatchLine
                        key={`${match.line}-${match.colStart}-${i}`}
                        filePath={file.filePath}
                        match={match}
                    />
                ))}
        </div>
    );
}

/* ── Match Line ── */

function MatchLine({
    filePath,
    match,
}: {
    filePath: string;
    match: import("@magic-md/engine-client/protocol").SearchMatch;
}) {
    const navigateToMatch = useSearchStore((s) => s.navigateToMatch);
    const badge = MATCH_KIND_BADGE[match.matchKind];
    const touch = useSearchPanelTouch();

    // Highlight matched text in the line
    const highlightedLine = useMemo(() => {
        const text = match.lineText;
        if (match.colStart >= text.length) return text;
        const before = text.slice(0, match.colStart);
        const matched = text.slice(match.colStart, match.colEnd);
        const after = text.slice(match.colEnd);
        return { before, matched, after };
    }, [match]);

    return (
        <button
            type="button"
            className={cn(
                "flex items-center w-full gap-2 hover:bg-muted/40 font-mono text-muted-foreground cursor-pointer text-left",
                touch ? "pl-10 pr-4 py-2 text-[12px]" : "pl-8 pr-3 py-0.5 text-[11px]",
            )}
            onClick={() => navigateToMatch(filePath, match)}
        >
            <span className={cn("text-muted-foreground/60 w-8 text-right shrink-0", touch ? "text-[10px]" : "text-[9px]")}>
                L{match.line}
            </span>
            {badge && (
                <span
                    className={cn("px-1 rounded shrink-0 theme-accent-chip", touch ? "text-[10px]" : "text-[9px]")}
                    style={getThemeAccentStyle(badge.accent)}
                >
                    {badge.label}
                </span>
            )}
            <span className="truncate">
                {typeof highlightedLine === "string" ? (
                    highlightedLine
                ) : (
                    <>
                        <span>{highlightedLine.before}</span>
                        <span
                            className="rounded-sm theme-accent-chip"
                            style={getThemeAccentStyle("warning")}
                        >
                            {highlightedLine.matched}
                        </span>
                        <span>{highlightedLine.after}</span>
                    </>
                )}
            </span>
        </button>
    );
}

/* ── Search Status ── */

function SearchStatus() {
    const t = useT("editor");
    const isSearching = useSearchStore((s) => s.isSearching);
    const totalMatches = useSearchStore((s) => s.totalMatches);
    const totalFiles = useSearchStore((s) => s.totalFiles);
    const searchDuration = useSearchStore((s) => s.searchDuration);
    const truncated = useSearchStore((s) => s.truncated);
    const touch = useSearchPanelTouch();

    if (totalMatches === 0 && !isSearching) return null;

    return (
        <div className={cn("border-t border-border text-muted-foreground flex items-center gap-2", touch ? "px-4 py-2.5 text-[12px]" : "px-3 py-1.5 text-[10px]")}>
            {isSearching && <Loader2 size={touch ? 12 : 10} className="animate-spin" />}
            <span>
                {t("search.status.results", {
                    matches: totalMatches,
                    files: totalFiles,
                })}
            </span>
            {searchDuration > 0 && !isSearching && (
                <span>· {searchDuration}ms</span>
            )}
            {truncated && (
                <span
                    className="ml-auto theme-accent-text"
                    style={getThemeAccentStyle("warning")}
                >
                    {t("search.status.truncated")}
                </span>
            )}
        </div>
    );
}

/* ── Main SearchPanel ── */

export function SearchPanel({
    autoFocus = true,
    className,
    showHeader = true,
    touch = false,
}: SearchPanelProps = {}) {
    const t = useT("editor");
    const query = useSearchStore((s) => s.query);
    const results = useSearchStore((s) => s.results);
    const isSearching = useSearchStore((s) => s.isSearching);
    const errorMessage = useSearchStore((s) => s.errorMessage);

    return (
        <SearchPanelTouchContext.Provider value={touch}>
            <div className={cn("flex flex-col h-full", className)}>
                {/* Header */}
                {showHeader ? (
                    <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
                        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                            {t("sidebar.search")}
                        </span>
                        <SidebarCloseButton />
                    </div>
                ) : null}

                <SearchInput autoFocus={autoFocus} />

                {/* Error message */}
                {errorMessage && (
                    <div
                        className={cn(
                            "border-b border-border theme-accent-banner",
                            touch ? "px-4 py-2 text-[12px]" : "px-3 py-1.5 text-[10px]",
                        )}
                        style={getThemeAccentStyle("error")}
                    >
                        {errorMessage}
                    </div>
                )}

                {/* Empty state */}
                {!isSearching &&
                    results.length === 0 &&
                    query.trim() !== "" &&
                    !errorMessage && (
                        <div className="flex-1 flex items-center justify-center p-4">
                            <div className={cn("text-center text-muted-foreground/60", touch ? "text-[13px]" : "text-[11px]")}>
                                <p>{t("search.empty")}</p>
                            </div>
                        </div>
                    )}

                {/* Results */}
                <SearchResultTree />

                {/* Status bar */}
                <SearchStatus />
            </div>
        </SearchPanelTouchContext.Provider>
    );
}
